/**
 * Q4 2026 sweepstakes campaign: a fully loaded Ford Raptor or $100,000 cash.
 *
 * Cycle 1 (Jeep Gladiator) spread the prize across copy strings, image
 * filenames, the "Earn X entries" module, the JSON-LD description and both FAQ
 * answers. Its entry mechanic then changed three times, and every change meant
 * hunting the same value in four places. Each surface reads this object
 * instead, so a prize, ratio or date change is an edit here and nowhere else.
 *
 * Everything here is request-free and safe in a client bundle. The clock read
 * that turns the schedule into a state lives in `../sweepstakes.ts`.
 *
 * @see https://linear.app/baerskin/issue/DEV-10667
 */
import { marketBaseOf } from "@local/i18n/src/languages"

// Campaign boundaries as UTC instants, each converted from Pacific local time
// on its own (B7 and B8 put the campaign on Pacific). 11 September falls in
// PDT (UTC-7), and DST ends on Sunday 1 November 2026, so the three later
// boundaries are PST (UTC-8). Cycle 1 applied one fixed offset to every
// boundary and its PR shipped with the DST assumption flagged as a known risk.
const ENTRY_OPENS_UTC_MS = Date.UTC(2026, 8, 11, 7) // 11 Sep 2026 00:00 PDT
const DOUBLE_ENTRIES_START_UTC_MS = Date.UTC(2026, 10, 9, 18) // 9 Nov 2026 10:00 PST
const ENTRY_CLOSES_UTC_MS = Date.UTC(2026, 11, 1, 7, 59) // 30 Nov 2026 23:59 PST
const WINNER_ANNOUNCED_UTC_MS = Date.UTC(2026, 11, 5, 8) // 5 Dec 2026 00:00 PST

export const SWEEPSTAKES = {
  prize: {
    qualifier: "fully loaded",
    name: "Ford Raptor",
    // The checkout quantity tiles are too narrow for the full name, so the
    // design shortens it there. Kept beside the name so a prize change is
    // still one edit.
    shortName: "Ford",
    cashAlternative: "$100,000",
  },
  entry: {
    // B2 dropped the CAD conversion: CAD $1 earns an entry on the same terms
    // as USD $1, so the ratio needs no per-currency table.
    minorUnitsPerEntry: 100,
    doubleMultiplier: 2,
  },
  markets: ["us", "ca"],
  // Regions of a campaign market that cannot enter (DEV-10670). Both forms are
  // listed because the geo headers disagree: Vercel sends the subdivision code
  // ("QC") and the Netlify blob sends its name ("Quebec").
  excludedRegions: ["qc", "quebec"],
  rulesUrl: "https://sharedsweeps.com/rules",
  // Alt text for the campaign hero, used when the CMS image carries none.
  // Version-controlled rather than left to data entry: cycle 1 shipped these
  // banners with alt text that recited the marketing copy, which DEV-9103 then
  // had to fix. It describes the picture and makes no offer.
  heroImageAlt: "A Ford Raptor pickup truck",
  // Product-page banner (DEV-10675), one pair of crops per state. The offer is
  // baked into the artwork, so the alt describes the picture and the
  // surrounding sr-only line carries the words. The 2x artwork adds a sticker
  // and a taller phone crop, hence per-pair dimensions.
  productBanner: {
    alt: "A white Ford Raptor pickup truck beside torn dark paper",
    open: {
      desktopSrc:
        "/sweepstacks/baerskin-FORD-sweepstakes-banner-desktop-01.jpg",
      desktopWidth: 1534,
      desktopHeight: 398,
      mobileSrc: "/sweepstacks/baerskin-FORD-sweepstakes-banner-mobile-01.jpg",
      mobileWidth: 742,
      mobileHeight: 292,
    },
    double: {
      desktopSrc:
        "/sweepstacks/baerskin-FORD-sweepstakes-banner-DOUBLE-ENTRIES-desktop-01.jpg",
      desktopWidth: 1534,
      desktopHeight: 398,
      mobileSrc:
        "/sweepstacks/baerskin-FORD-sweepstakes-banner-DOUBLE-ENTRIES-mobile-01.jpg",
      mobileWidth: 742,
      mobileHeight: 382,
    },
  },
  // Prize-card photos on the campaign page (DEV-10680). The cash one carries
  // the cheque and an "our previous winner" badge in the artwork, so its alt
  // says who is in it.
  prizeImages: {
    width: 630,
    height: 301,
    vehicleSrc: "/sweepstacks/ford.jpg",
    vehicleAlt: "A white Ford Raptor pickup truck on a mountain road",
    cashSrc: "/sweepstacks/cash.jpg",
    cashAlt: "The previous winner holding a $100,000 cheque from Shared Sweeps",
  },
  termsNote: "Terms and conditions apply.",
  schedule: {
    entryOpensUtcMs: ENTRY_OPENS_UTC_MS,
    doubleEntriesStartUtcMs: DOUBLE_ENTRIES_START_UTC_MS,
    entryClosesUtcMs: ENTRY_CLOSES_UTC_MS,
    winnerAnnouncedUtcMs: WINNER_ANNOUNCED_UTC_MS,
  },
} as const

/**
 * Winner assets, filled in after the draw (DEV-10682).
 *
 * Every field is nullable and every one degrades safely: with no image the
 * announcement falls back to the campaign artwork, and with no quote the quote
 * block is not rendered. So the template can ship before 5 December and
 * swapping the real assets in is an edit here rather than a code change.
 *
 * `quote` is deliberately null rather than a placeholder. Words attributed to
 * a customer are the one placeholder that must never reach production.
 */
export const SWEEPSTAKES_WINNER: {
  imageDesktopSrc: string | null
  imageMobileSrc: string | null
  imageAlt: string | null
  quote: string | null
  quoteAttribution: string | null
} = {
  imageDesktopSrc: null,
  imageMobileSrc: null,
  imageAlt: null,
  quote: null,
  quoteAttribution: null,
}

// Every date the copy quotes comes from the schedule, so a date change cannot
// leave a sentence behind. All of them format in Pacific: the close instant is
// 1 December in UTC and would otherwise read as the wrong day.
const PACIFIC = "America/Los_Angeles"

const ordinalSuffix = (day: number): string => {
  if (day % 100 >= 11 && day % 100 <= 13) {
    return "th"
  }
  switch (day % 10) {
    case 1:
      return "st"
    case 2:
      return "nd"
    case 3:
      return "rd"
    default:
      return "th"
  }
}

const pacificDate = (
  utcMs: number,
  month: "long" | "short"
): { month: string; day: number } => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: PACIFIC,
    month,
    day: "numeric",
  }).formatToParts(utcMs)

  return {
    month: parts.find((part) => part.type === "month")?.value ?? "",
    day: Number(parts.find((part) => part.type === "day")?.value ?? 0),
  }
}

/** "November 30th" / "Nov 30th", as the agency's copy writes dates. */
const dateLabel = (
  utcMs: number,
  month: "long" | "short",
  upperMonth = false
): string => {
  const { month: name, day } = pacificDate(utcMs, month)
  return `${upperMonth ? name.toUpperCase() : name} ${day}${ordinalSuffix(day)}`
}

export const SWEEPSTAKES_DATES = {
  opensLong: dateLabel(SWEEPSTAKES.schedule.entryOpensUtcMs, "long"),
  closesLong: dateLabel(SWEEPSTAKES.schedule.entryClosesUtcMs, "long"),
  closesShort: dateLabel(SWEEPSTAKES.schedule.entryClosesUtcMs, "short"),
  // The agency shouts the month and leaves the ordinal alone, as in cycle 1's
  // "DOUBLE ENTRIES NOW LIVE UNTIL JUNE 4th!".
  closesLongUpperMonth: dateLabel(
    SWEEPSTAKES.schedule.entryClosesUtcMs,
    "long",
    true
  ),
  winnerLong: dateLabel(SWEEPSTAKES.schedule.winnerAnnouncedUtcMs, "long"),
  // The legal text dates its clauses, so the year comes from the schedule too.
  year: new Intl.DateTimeFormat("en-US", {
    timeZone: PACIFIC,
    year: "numeric",
  }).format(SWEEPSTAKES.schedule.entryClosesUtcMs),
} as const

export type SweepstakesState =
  "pending" | "open" | "double" | "closed" | "winner"

/** US and Canada. Language-variant slugs normalise to their base market. */
export const isSweepstakesMarket = (market: string): boolean =>
  (SWEEPSTAKES.markets as readonly string[]).includes(marketBaseOf(market))

// Shared by both announcement lines so renaming the prize stays one edit.
const PRIZE_PHRASE = `a ${SWEEPSTAKES.prize.qualifier} ${SWEEPSTAKES.prize.name}, or ${SWEEPSTAKES.prize.cashAlternative} cash`

/**
 * The agency-approved announcement lines, keyed by campaign state. The comma
 * before "or" is theirs.
 *
 * Stored in sentence case, not the capitals the tickets quote: the topbar
 * uppercases in CSS (`TopPromoBanner`), and the same copy has to read
 * correctly in the FAQ and the footer, which do not.
 *
 * `closed` and `winner` have no entry, because the banner is removed in both
 * (DEV-10671).
 */
export const SWEEPSTAKES_ANNOUNCEMENT = {
  open: `Stand a chance to win ${PRIZE_PHRASE}!`,
  double: `Double entries are live! Stand a chance to win ${PRIZE_PHRASE} - ends ${SWEEPSTAKES_DATES.closesShort}!`,
} as const

// Keeps the lookup total over the state union: the states with no line above
// are exactly the states that announce nothing.
const ANNOUNCEMENT: Partial<Record<SweepstakesState, string>> =
  SWEEPSTAKES_ANNOUNCEMENT

/**
 * The store's standing free-shipping promise, as a topbar template: the
 * placeholders resolve per market through `formatTopbarTemplate`.
 */
export const SWEEPSTAKES_FREE_SHIPPING_CLAUSE =
  "Free Shipping Over %%CURRENCY%%%%FREE_SHIPPING_THRESHOLD%%"

/**
 * The same promise for a surface that ships free at any basket value, which is
 * every channel except the store (INC-819). It carries no placeholder, because
 * there is no threshold to name.
 */
export const SWEEPSTAKES_FREE_SHIPPING_CLAUSE_FLAT = "Free Shipping"

/**
 * The campaign line with the free-shipping promise added before the closing
 * "!", so the two read as one sentence.
 */
export const announcementWithFreeShipping = (
  line: string,
  clause: string
): string =>
  line.endsWith("!")
    ? `${line.slice(0, -1)} + ${clause}!`
    : `${line} + ${clause}`

/**
 * The banner crops a state shows: the 2x artwork during the double window, the
 * base artwork in every other state.
 */
export const productBannerFor = (state: SweepstakesState) =>
  state === "double"
    ? SWEEPSTAKES.productBanner.double
    : SWEEPSTAKES.productBanner.open

/** The line a state announces, or null when it announces nothing. */
export const announcementFor = (state: SweepstakesState): string | null =>
  ANNOUNCEMENT[state] ?? null

/**
 * Whether the campaign is running. `pending` is before it opens, `closed` and
 * `winner` after, and every campaign surface is retired in all three.
 *
 * The two per-state maps in this file are keyed by exactly these states. Use
 * this to gate a surface: asking whether a state announces a line happens to
 * give the same answer, but it reads as a question about copy.
 */
export const isSweepstakesLive = (state: SweepstakesState): boolean =>
  state === "open" || state === "double"

/**
 * The entry close date as the legal line writes it, derived from the schedule
 * so a date change cannot leave the disclaimer behind. The instant is 1
 * December in UTC, hence the Pacific timezone.
 */
export const closeDateNumeric = (): string =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  }).format(SWEEPSTAKES.schedule.entryClosesUtcMs)

/**
 * The rules URL as visible link text: the host and the path, no scheme. The
 * site terms show it beside the link, so a changed rules host cannot leave
 * that legal section naming the old one.
 */
export const SWEEPSTAKES_RULES_LINK_TEXT = `${new URL(SWEEPSTAKES.rulesUrl).host}${new URL(SWEEPSTAKES.rulesUrl).pathname}`

/**
 * The agency's footer disclaimer, split around the rules link because that
 * link is an element rather than markup in a string.
 *
 * B11: go live with the cycle-1 wording, which already carries the right end
 * date, and swap it when the agency sends Q4 text. Keeping both halves here
 * means that swap is an edit in this file. The Quebec carve-out is theirs and
 * must survive verbatim.
 */
export const sweepstakesLegal = () => ({
  heading: `${SWEEPSTAKES.prize.shortName} promotion`,
  headingSponsor: "Powered By Shared Sweeps",
  beforeRulesLink: `NO PURCHASE NECESSARY. Ends ${closeDateNumeric()}. Must be: i) a legal US resident of the 50 US or DC and age of majority or ii) a legal resident of Canada (excl. Quebec) and age of majority at time of entry. Subject to Official Rules available at `,
  afterRulesLink: ". Void where prohibited. Sponsored By Shared Sweeps.",
})

/** Whether a geo region is carved out of an otherwise eligible market. */
export const isExcludedRegion = (region: string | null | undefined): boolean =>
  typeof region === "string" &&
  (SWEEPSTAKES.excludedRegions as readonly string[]).includes(
    region.trim().toLowerCase()
  )

/**
 * The state the schedule puts a given instant in.
 *
 * Lives here rather than beside `SWEEPSTAKES_STATE` because the client hook
 * re-resolves it in the browser: this module reads no clock of its own, so
 * importing it costs a caller nothing.
 *
 * @param nowMs Instant to place, in epoch milliseconds.
 * @returns The campaign state that instant falls in.
 */
export const stateAt = (nowMs: number): SweepstakesState => {
  const { schedule } = SWEEPSTAKES
  if (nowMs < schedule.entryOpensUtcMs) {
    return "pending"
  }
  if (nowMs < schedule.doubleEntriesStartUtcMs) {
    return "open"
  }
  if (nowMs <= schedule.entryClosesUtcMs) {
    return "double"
  }
  if (nowMs < schedule.winnerAnnouncedUtcMs) {
    return "closed"
  }
  return "winner"
}

/**
 * The scheduled campaign state, resolved once when this module loads.
 *
 * `cacheComponents` is on, which forbids reading the clock during render, so
 * the state cannot be derived per request. Cold starts and ISR revalidations
 * on fresh containers pick a new state up on their own, while warm containers
 * and already-cached segments keep serving the previous one until they cycle.
 * Landing a transition on its exact timestamp needs the scheduled revalidation
 * still open on DEV-10668.
 */
export const SWEEPSTAKES_STATE: SweepstakesState = stateAt(Date.now())

/**
 * Whether a surface should show the campaign: the market is in scope and the
 * state is live. Every campaign surface asks this one question.
 */
export const isSweepstakesActive = (
  market: string,
  state: SweepstakesState
): boolean => isSweepstakesMarket(market) && isSweepstakesLive(state)

/**
 * Whether this visitor may see the campaign page at all (DEV-10670).
 *
 * The page is the one campaign surface an excluded visitor cannot simply be
 * shown less of: a client-side guard would leave a Quebec shopper on an empty
 * screen. `proxy.ts` calls this instead and redirects them, which is also why
 * the region arrives as an argument rather than through a cookie.
 *
 * @param market Market slug, base or language variant.
 * @param region Geo region from the request headers.
 * @returns True when the market is in scope and the region is not carved out.
 */
export const isSweepstakesPageAllowed = (
  market: string,
  region: string | null | undefined
): boolean => isSweepstakesMarket(market) && !isExcludedRegion(region)

// How many entries a currency unit earns per state. A state absent here earns
// none, which is what retires the badge in `pending`, `closed` and `winner`.
const ENTRY_MULTIPLIER: Partial<Record<SweepstakesState, number>> = {
  open: 1,
  double: SWEEPSTAKES.entry.doubleMultiplier,
}

/**
 * Entries a single price earns, or null when it earns none.
 *
 * The one place the mechanic is expressed. Cycle 1 computed it in the badge
 * while the page and the FAQ described it in prose, and on 8 May the badge
 * read "EARN 7 ENTRIES" beside a page saying "Your order is your entry".
 *
 * @param minorUnits Price in minor currency units. CAD and USD share the rate
 * (B2), so no conversion happens here.
 * @param state Campaign state deciding the multiplier.
 * @returns Whole entries, or null when the state or the price earns nothing.
 */
export const entriesFor = (
  minorUnits: number,
  state: SweepstakesState
): number | null => {
  const multiplier = ENTRY_MULTIPLIER[state]
  if (multiplier === undefined || minorUnits <= 0) {
    return null
  }

  const entries =
    Math.floor(minorUnits / SWEEPSTAKES.entry.minorUnitsPerEntry) * multiplier
  return entries > 0 ? entries : null
}

/**
 * Entries copy for a count, shared by the badge (DEV-10675) and the "Earn X
 * entries" module under the best sellers (DEV-10674). One template, so the
 * two surfaces on the same page cannot word the same mechanic differently.
 *
 * Wording is the agency's, quoted verbatim in DEV-10674. Note that the number
 * under-reports: entries accrue per order on revenue plus tax (D-1535), so tax
 * adds entries no tile ever saw, and a basket's floor is not the sum of each
 * item's floor (two $10.50 items show 10 and 10, the order earns 21). Changing
 * the copy to account for that is a change request against the scoping doc,
 * not a call to make here.
 */
export const entriesBadgeCopy = (entries: number): string =>
  `Earn ${entries} entries to win a ${SWEEPSTAKES.prize.name}*`

/**
 * Entries copy for a checkout quantity tile, which is narrow enough that the
 * design drops "Earn" and shortens the prize. The tile renders the leading
 * "+" itself, the same way it does for its free-shipping line.
 */
export const entriesTierCopy = (entries: number): string =>
  `${entries} entries to win a ${SWEEPSTAKES.prize.shortName}*`

// The FAQ answer, assembled once. Only the opening clause separates the store
// from a landing page, which DEV-10678 asks for explicitly rather than two
// strings that drift, and only the entry phrase and the prefix separate `open`
// from `double`.
const FAQ_OPENING = {
  store: `Buy any product on BAERSkintactical.com`,
  lp: `Purchase this product`,
} as const

const faqBody = (state: SweepstakesState): string =>
  [
    `between ${SWEEPSTAKES_DATES.opensLong} and ${SWEEPSTAKES_DATES.closesLong}`,
    `and every dollar you spend automatically enters you.`,
    state === "double"
      ? `Every additional dollar spent is another ${SWEEPSTAKES.entry.doubleMultiplier}x entries.`
      : `Every additional dollar spent is another entry.`,
    `The winner will be announced on ${SWEEPSTAKES_DATES.winnerLong}`,
    `and gets to choose between the ${SWEEPSTAKES.prize.qualifier} ${SWEEPSTAKES.prize.name} or ${SWEEPSTAKES.prize.cashAlternative} cash.`,
    `Open to customers in the US and Canada only.`,
    `No purchase necessary - for alternative method of entry, full terms and official rules, visit ${new URL(SWEEPSTAKES.rulesUrl).host}.`,
  ].join(" ")

/**
 * The campaign's FAQ entry for a channel, or null when the market is out of
 * scope or the state has no answer.
 *
 * `closed` has no entry yet. DEV-10678 leaves that past-tense wording to be
 * drafted with Luke, so rather than invent it the question disappears when
 * entries close, which is how every other campaign surface behaves.
 *
 * @param market Market slug, base or language variant.
 * @param channel Channel slug; "store" takes the store opening clause.
 * @param state Campaign state deciding the entry phrase and the prefix.
 */
export const sweepstakesFaqItem = (
  market: string,
  channel: string,
  state: SweepstakesState
): { id: string; question: string; answer: string } | null => {
  if (!isSweepstakesMarket(market) || !isSweepstakesLive(state)) {
    return null
  }

  const opening = channel === "store" ? FAQ_OPENING.store : FAQ_OPENING.lp
  const prefix =
    state === "double"
      ? `DOUBLE ENTRIES NOW LIVE UNTIL ${SWEEPSTAKES_DATES.closesLongUpperMonth}! `
      : ""

  return {
    id: "sweepstakes-faq",
    question: `How do I enter to win the ${SWEEPSTAKES.prize.name} or ${SWEEPSTAKES.prize.cashAlternative} cash?`,
    answer: `${prefix}${opening} ${faqBody(state)}`,
  }
}
